/*
 *                     NOTICE
 *        COPYRIGHT 2019 - 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import "@abinitio/kendo.all";
import { TSOGClassRegistration } from "abweb/dto/generated/TSOGClassRegistration";
import { HealthApiServices } from "abweb/generated/abwebinternal/api/health/HealthApiServices";
import { AbInternalMiscApiServices } from "abweb/generated/abwebinternal/api/misc/AbInternalMiscApiServices";
import { AboutService } from "abweb/service/about/AboutService";
import { AbWebServices } from "abweb/service/AbWebServices";
import { AuthenticationService } from "abweb/service/authentication/AuthenticationService";
import { CSRFService } from "abweb/service/csrf/CSRFService";
import { DebugState } from "abweb/service/debug/DebugState";
import { DataSourceValueInfoService } from "abweb/service/dsinfo/DataSourceValueInfoService";
import { I18NService } from "abweb/service/i18n/I18NService";
import { AbSDKImageIdentifierRegistry } from "abweb/service/image/AbSDKImageInfo";
import { AbWebImageIdentifierRegistry } from "abweb/service/image/AbWebImageIdentifierRegistry";
import { DefaultImageIdentifierManager } from "abweb/service/image/api/DefaultImageIdentifierManager";
import { SimpleImageIdentifierService } from "abweb/service/image/SimpleImageIdentifierService";
import { KeyboardService } from "abweb/service/keyboard/KeyboardService";
import { LocaleManager } from "abweb/service/locale/LocaleManager";
import { LocaleService } from "abweb/service/locale/LocaleService";
import { TranslatorManager } from "abweb/service/locale/TranslatorManager";
import { NotificationService } from "abweb/service/notification/NotificationService";
import { HelpService } from "abweb/service/onlineHelp/HelpService";
import { LocalPersonaService } from "abweb/service/persona/LocalPersonaService";
import { PackageForSupportService } from "abweb/service/pfs/PackageForSupportService";
import { ProductService } from "abweb/service/product/ProductService";
import { AbSDKRouter } from "abweb/service/router/absdkRouter/AbSDKRouter";
import { RPCService } from "abweb/service/rpc/RPCService";
import { ClientSessionService } from "abweb/service/session/ClientSessionService";
import { SessionData } from "abweb/service/SessionData";
import { UserSettingsService } from "abweb/service/settings/UserSettingsService";
import { LocalUserNavigationService } from "abweb/service/space/LocalUserNavigationService";
import { TeardownService } from "abweb/service/teardown/TeardownService";
import { UserStateProvider } from "abweb/service/userState/UserStateProvider";
import { BrowserSpecificBugSituations } from "abweb/service/utils/BrowserSpecficBugSituations";
import { AbClipboard } from "abweb/utils/clipboard/AbClipboard";
import { EvaluationContext } from "abweb/utils/expression/EvaluationContext";
import { guid } from "abweb/utils/GuidUtils";
import { InterfaceUtils } from "abweb/utils/InterfaceUtils";
import { KendoLocaleUtils } from "abweb/utils/kendo/KendoLocaleUtils";
import { StringUtils } from "abweb/utils/StringUtils";
import { URLUtils } from "abweb/utils/URLUtils";
import { configure } from "mobx";
/**
 * Configure all AbWebServices needed by the application
 */
export class AbWebServicesInitializer {
  /*
   * This initialization logic was separated out from a static AbWebServices.initDefaultServices as was getting a circular module dependency
   * that caused some modules to not load correctly.
   * The circular dependency was:  AbTemplateComponent -> AbWebServices -> AbApplication,
   * and AbApplication is an AbTemplate component subclass
   */
  static async initDefaultServices(webServicesConfig) {
    // Use 'data struct' to simplify gradually building all arg values of the AbWebServices constructor
    // through an async promise chain
    const wsArgs = {};
    await AbWebServicesInitializer.configureCoreServices(wsArgs, webServicesConfig);
    new AbWebServices(wsArgs.localeService, wsArgs.i18NService, wsArgs.router, wsArgs.sessionData, wsArgs.rpcService, wsArgs.authenticationService, wsArgs.clientSessionService, wsArgs.productService, wsArgs.aboutService, wsArgs.imageIdentifierRegistry, wsArgs.imageIdentifierManager, wsArgs.dataSourceValueInfoService, wsArgs.packageForSupportService, wsArgs.clipboard, wsArgs.csrfTokenService, wsArgs.teardownService, wsArgs.notificationService, wsArgs.keyboardService, wsArgs.helpService, DebugState.instance, HealthApiServices.getHealthApi.bind(HealthApiServices), wsArgs.personaService, wsArgs.userNavigationService, wsArgs.userState, wsArgs.userSettingsService);
    HealthApiServices.initDefaultServices();
    AbInternalMiscApiServices.initDefaultServices();
    AbWebServicesInitializer.addForbiddenIconSupport();
  }
  static addForbiddenIconSupport() {
    // Set up CSS declarations for custom cursors - ABSDK-1640
    // Cursor for disabled buttons: Get the path of an absdk icon
    const identifierInfo = AbWebServices.getImageIdentifierManager().getIdentifierInfo("absdk:forbidden-cursor");
    const iconFilePath = identifierInfo.scales[0].path;
    // Add a style to the body element that sets the CSS variable --ab-global-cursor-not-allowed to the icon we got above
    // eslint-disable-next-line abinitio/no-constant-css-variable-reassignment-eslint
    document.body.style.setProperty("--ab-global-cursor-not-allowed", `url('${iconFilePath}'), not-allowed`);
  }
  static configureCoreServices(wsArgs, overrideConfig) {
    // Meta-services: other service initialization depend on these being available
    wsArgs.teardownService = new TeardownService();
    // Do interfaceExtends first in case any initialization logic depends on it
    AbWebServicesInitializer.interfaceExtends(); // for InterfaceUtils.{is,as,etc} to operate correctly

    // CAT-769: Monkey patch kendo.guid function to be much more efficient.  Note can still get access to
    // original kendo guid function if desired via exported GuidUtils.origKendoGuid function.
    kendo.guid = guid;
    const indexDepth = overrideConfig.indexSubdirDepthFromDeployedAppRoot !== undefined ? overrideConfig.indexSubdirDepthFromDeployedAppRoot : 1; //default: /spa/index*.html
    URLUtils.configureURLUtils(indexDepth);

    // CAT-1829: Detect browser/version pairs and add a class to the root document.
    BrowserSpecificBugSituations.detect();
    const rpcService = new RPCService();
    if (overrideConfig.authenticationStrategy) {
      rpcService.authenticationStrategy = overrideConfig.authenticationStrategy;
    }
    wsArgs.rpcService = rpcService;
    return AbWebServicesInitializer.configureLocaleServices(wsArgs, rpcService).then(() => {
      ///// Security related configuration
      wsArgs.authenticationService = new AuthenticationService(rpcService);
      wsArgs.clientSessionService = new ClientSessionService(rpcService, wsArgs.teardownService);
      wsArgs.sessionData = new SessionData();
      wsArgs.productService = new ProductService(rpcService);
      wsArgs.aboutService = new AboutService(rpcService);
      wsArgs.csrfTokenService = overrideConfig != null && overrideConfig.csrfServiceCreator != null ? overrideConfig.csrfServiceCreator(rpcService) : new CSRFService(rpcService);

      ///// Misc Services
      wsArgs.router = new AbSDKRouter();
      wsArgs.packageForSupportService = new PackageForSupportService(rpcService);
      AbWebServicesInitializer.configureImageServices(wsArgs, overrideConfig.imageInstallRoot, overrideConfig.imageIdentifierManager);
      AbWebServicesInitializer.configureEvalutionContext(wsArgs.i18NService);
      wsArgs.clipboard = overrideConfig.clipboard != null ? overrideConfig.clipboard : new AbClipboard();

      ///// Data related service configuration
      wsArgs.dataSourceValueInfoService = new DataSourceValueInfoService();
      wsArgs.notificationService = new NotificationService();
      wsArgs.keyboardService = new KeyboardService();
      wsArgs.helpService = overrideConfig?.helpService || new HelpService();
      wsArgs.personaService = overrideConfig?.personaService || new LocalPersonaService();
      wsArgs.userNavigationService = overrideConfig?.userNavigationService || new LocalUserNavigationService();
      wsArgs.userState = new UserStateProvider();
      wsArgs.userSettingsService = overrideConfig?.userSettingsService || new UserSettingsService();

      // This is the default level of MobX strictness.  To be stricter, we should consider
      // configure({
      //     enforceActions: "always",
      //     computedRequiresReaction: true,
      //     reactionRequiresObservable: true,
      //     observableRequiresReaction: true,
      //     disableErrorBoundaries: true
      // });  See https://mobx.js.org/configuration.html#linting-options for details.
      configure({
        enforceActions: "observed"
      });
      TSOGClassRegistration.registerClasses(); // register ts classes that are generated by the ts code generator
    });
  }

  // Sets up initial pre-login locales, which may be updated after user logs in via locale listeners
  static async configureLocaleServices(wsConfig, rpcService) {
    // KendoLocaleUtils is 'special' in that needs to be registered before ANY kendo component is used (regardless of
    // whether KendoLocalUtils has been loaded or not). So directly register the locale listener.
    // NumberUtils and Date{Time}Utils don't have this issue (have to import them in order to use them).
    await LocaleManager.registerAndInvokeLocaleListener(KendoLocaleUtils.onLocaleChange.bind(KendoLocaleUtils));

    // Load language locale dependent resource bundles
    // Init i18n resources first so that any messages or errors during service initialization *could* be localized
    const locale = LocaleManager.languageLocale;
    const tm = new TranslatorManager(locale);
    LocaleManager.registerLocaleListener(localeState => tm.reloadResourceBundles(localeState.languageLocale));
    await tm.loadResourceBundles(["webapp/abweb/assets/locale/{locale}/abweb.json"], []);
    wsConfig.localeService = new LocaleService(rpcService, tm);
    const i18NService = new I18NService(tm.translator);
    i18NService.registerUserSettings();
    wsConfig.i18NService = i18NService;
  }
  static configureImageServices(wsConfig, customInstallRoot, customIIMgr) {
    // Only tests should ever need to override the installRoot
    const installRoot = customInstallRoot ?? URLUtils.getAbsdkRelativePath("webapp/abweb");
    wsConfig.imageIdentifierRegistry = new AbWebImageIdentifierRegistry(installRoot);
    let imageIdentifierManager = customIIMgr;
    if (imageIdentifierManager == null) {
      imageIdentifierManager = new DefaultImageIdentifierManager();
      imageIdentifierManager.setImageIdentifierService(new SimpleImageIdentifierService(wsConfig.imageIdentifierRegistry));
    }
    // Add the AbSDK images
    const absdkImageIdentifierRegistry = new AbSDKImageIdentifierRegistry(installRoot, "absdk");
    imageIdentifierManager.getImageIdentifierService().mergeRegistries(absdkImageIdentifierRegistry);
    wsConfig.imageIdentifierManager = imageIdentifierManager;
  }
  static configureEvalutionContext(i18n) {
    const ec = new EvaluationContext();
    ec.set("i18n", (resourceBundleKey, msgKey, ...msgArgs) => i18n.getMessage(resourceBundleKey, msgKey, msgArgs));
    // CAT-187: marker fn for implementations that don't want to i18n (yet or ever)
    ec.set("plainText", (msg, ...msgArgs) => StringUtils.sprintf(msg, ...msgArgs));
    EvaluationContext.setBaseEvaluationContext(ec);
  }

  /**
   * Give InterfaceUtils runtime awareness (for methods 'is', 'as', etc) of all interface extensions.
   */
  static interfaceExtends() {
    InterfaceUtils.interfaceExtends("IUIMetaData", "IRectangularMetaData");
    InterfaceUtils.interfaceExtends("IRouter", "IAbEventDispatcher");
    InterfaceUtils.interfaceExtends("IEditableContainer", "IEditableComponent");
    InterfaceUtils.interfaceExtends("IAbDialogComponent", "IAbPopupComponent");
    InterfaceUtils.interfaceExtends("INodeRenderer", "IAbComponent");
  }
}

// Strongly typed property bag for AbWebServices constructor arguments
//# sourceMappingURL=AbWebServicesInitializer.js.map
